/**
 * Leitner spaced-repetition scheduler.
 *
 * Five boxes (1-5). Cards in box `b` are due every `INTERVALS_DAYS[b-1]`
 * days from the last review. A correct answer promotes the card up one
 * box (cap 5). A miss demotes it back to box 1. New cards (no progress
 * recorded) are always due.
 *
 * Persistence: per-deck progress map in `localStorage` under
 * `elementpad:learn:<deckId>`. Storage is intentionally per-deck so
 * deleting one deck's progress doesn't nuke the others, and so writes
 * stay small.
 *
 * No backend, no accounts - matches the project's "no AI in v1, no
 * server-side tracking" rules.
 */

/** Day-based intervals for each box (index = box - 1). */
export const INTERVALS_DAYS = [0, 1, 3, 7, 16] as const;
export const MAX_BOX = 5;
const DAY_MS = 24 * 60 * 60 * 1000;

export type LeitnerGrade = "again" | "hard" | "good" | "easy";

export interface CardProgress {
  /** Current Leitner box (1-5). */
  box: number;
  /** Epoch ms of last review; 0 means never reviewed. */
  lastReviewedAt: number;
  /** Lifetime correct/miss counts - useful for stats and recap. */
  correct: number;
  missed: number;
}

export type DeckProgress = Record<string, CardProgress>;

const STORAGE_PREFIX = "elementpad:learn:";

export function storageKey(deckId: string): string {
  return STORAGE_PREFIX + deckId;
}

export function loadProgress(deckId: string): DeckProgress {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(deckId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as DeckProgress) : {};
  } catch {
    return {};
  }
}

export function saveProgress(deckId: string, progress: DeckProgress): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(deckId), JSON.stringify(progress));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearProgress(deckId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(deckId));
  } catch {
    /* ignore */
  }
}

/**
 * Returns true when a card should be presented for review now.
 * Unseen cards (no progress) are always due.
 */
export function isDue(progress: CardProgress | undefined, now: number = Date.now()): boolean {
  if (!progress || progress.lastReviewedAt === 0) return true;
  const intervalDays = INTERVALS_DAYS[Math.min(progress.box, MAX_BOX) - 1];
  return now - progress.lastReviewedAt >= intervalDays * DAY_MS;
}

/**
 * Apply a grade to a card and return the new progress entry.
 * Pure function - caller is responsible for persistence.
 */
export function applyGrade(
  prev: CardProgress | undefined,
  grade: LeitnerGrade,
  now: number = Date.now(),
): CardProgress {
  const base: CardProgress = prev ?? { box: 1, lastReviewedAt: 0, correct: 0, missed: 0 };
  let nextBox = base.box;
  let correct = base.correct;
  let missed = base.missed;

  switch (grade) {
    case "again":
      nextBox = 1;
      missed += 1;
      break;
    case "hard":
      // Stay in the current box but count as a (weak) success.
      nextBox = Math.max(1, base.box);
      correct += 1;
      break;
    case "good":
      nextBox = Math.min(MAX_BOX, base.box + 1);
      correct += 1;
      break;
    case "easy":
      // Skip a box - reward fast confident answers.
      nextBox = Math.min(MAX_BOX, base.box + 2);
      correct += 1;
      break;
  }

  return { box: nextBox, lastReviewedAt: now, correct, missed };
}

/** Summary used by the deck list to show "X due / Y total / Z mastered". */
export interface DeckSummary {
  total: number;
  due: number;
  mastered: number;
  /** Average box (1-5) across all cards - rough mastery indicator. */
  averageBox: number;
}

export function summarize(
  cardIds: ReadonlyArray<string>,
  progress: DeckProgress,
  now: number = Date.now(),
): DeckSummary {
  let due = 0;
  let mastered = 0;
  let boxSum = 0;
  for (const id of cardIds) {
    const p = progress[id];
    const box = p?.box ?? 1;
    boxSum += box;
    if (box >= MAX_BOX) mastered += 1;
    if (isDue(p, now)) due += 1;
  }
  return {
    total: cardIds.length,
    due,
    mastered,
    averageBox: cardIds.length ? boxSum / cardIds.length : 0,
  };
}

/**
 * Select the next set of cards to study in a session, prioritizing due
 * cards. Falls back to the lowest-box cards once all due cards are
 * exhausted, so a session always has work.
 */
export function pickSession(
  cardIds: ReadonlyArray<string>,
  progress: DeckProgress,
  size: number,
  now: number = Date.now(),
): string[] {
  const due = cardIds.filter((id) => isDue(progress[id], now));
  if (due.length >= size) return due.slice(0, size);
  const rest = cardIds
    .filter((id) => !isDue(progress[id], now))
    .sort((a, b) => (progress[a]?.box ?? 1) - (progress[b]?.box ?? 1));
  return [...due, ...rest.slice(0, size - due.length)];
}